import { memo } from "react"
import { type PathParam, useParams } from "react-router"
import { useMutation } from "@tanstack/react-query"
import { AddRounded } from "@mui/icons-material"
import { IconButton } from "@mui/material"
import type { TagsDocType } from "@oboku/shared"
import type { DeepReadonlyObject } from "rxdb"
import { NotFoundPage } from "../../../../common/NotFoundPage"
import { Page } from "../../../../common/Page"
import { TopBarNavigation } from "../../../../navigation/TopBarNavigation"
import {
  useAddTagToBook,
  useRemoveTagFromBook,
} from "../../../../books/helpers"
import { useBook } from "../../../../books/states"
import { useTags } from "../../../../tags/helpers"
import { SelectableTagList } from "../../../../tags/tagList/SelectableTagList"
import { openAddTagDialog } from "../../../../tags/AddTagDialog"
import { EntitySelectionView } from "../../../../common/selection"
import { notify } from "../../../../notifications/toasts"
import type { ROUTES } from "../../../../navigation/routes"

const getTagSearchableText = (tag: DeepReadonlyObject<TagsDocType>) =>
  tag.name ?? ""

const EMPTY_TAGS: DeepReadonlyObject<TagsDocType>[] = []
const listStyle = { flex: 1 }
const withoutGlobalErrorToast = { meta: { suppressGlobalErrorToast: true } }

export const BookTagsScreen = memo(function BookTagsScreen() {
  const { id: bookId } = useParams<PathParam<typeof ROUTES.BOOK_TAGS>>()
  const { data: tags = EMPTY_TAGS } = useTags()
  const { data: book } = useBook({ id: bookId })
  const { mutateAsync: addTagToBook } = useAddTagToBook(withoutGlobalErrorToast)
  const { mutateAsync: removeTagFromBook } = useRemoveTagFromBook(
    withoutGlobalErrorToast,
  )

  const { mutate: save, isPending: isSaving } = useMutation({
    mutationFn: async ({
      toAdd,
      toRemove,
    }: {
      toAdd: string[]
      toRemove: string[]
    }) => {
      if (!bookId) return
      await Promise.all([
        ...toAdd.map((tagId) => addTagToBook({ _id: bookId, tagId })),
        ...toRemove.map((tagId) => removeTagFromBook({ _id: bookId, tagId })),
      ])
    },
    onSuccess: () => {
      notify("actionSuccess")
    },
  })

  if (!bookId || book === null) return <NotFoundPage />

  return (
    <Page sx={{ overflow: "hidden" }} bottomGutter={false}>
      <TopBarNavigation title="Manage tags" showBack />
      <EntitySelectionView
        searchPlaceholder="Search tags…"
        searchAriaLabel="Search tags"
        items={tags}
        persistedIds={book?.tags}
        entityKey={bookId}
        getSearchableText={getTagSearchableText}
        isSaving={isSaving}
        onSave={save}
        toolbarActions={
          <IconButton
            onClick={openAddTagDialog}
            color="primary"
            disabled={isSaving}
            aria-label="Create a new tag"
          >
            <AddRounded />
          </IconButton>
        }
        renderList={({ filteredIds, selectedItems, toggleSelection }) => (
          <SelectableTagList
            style={listStyle}
            data={filteredIds}
            selected={selectedItems}
            onItemClick={({ id: tagId }: { id: string; selected: boolean }) => {
              toggleSelection(tagId)
            }}
          />
        )}
      />
    </Page>
  )
})
